import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { 
  Mail, Plus, Search, Filter, Eye, Edit2, Trash2, Check, Clock,
  FileText, Calendar, Users, BarChart, HelpCircle, Settings,
  ChevronRight, Sun, Moon, RefreshCw
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  proposals: FileText,
  events: Calendar,
  crm_renewals: Users,
  reporting: BarChart,
  support: HelpCircle,
  internal: Settings
};

const categoryLabels = {
  proposals: 'Proposals',
  events: 'Events',
  crm_renewals: 'CRM & Renewals',
  reporting: 'Reporting',
  support: 'Support',
  internal: 'Internal'
};

const SystemEmailTemplates = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showPreview, setShowPreview] = useState(null);
  const [previewTheme, setPreviewTheme] = useState('dark');
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-emails/templates`, { 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-emails/categories`, { 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const initializeTemplates = async () => {
    setInitializing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-emails/initialize`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        fetchTemplates();
        fetchCategories();
      }
    } catch (error) {
      console.error('Failed to initialize templates:', error);
    } finally {
      setInitializing(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-emails/templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        fetchTemplates();
        fetchCategories();
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          template.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || template.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Group templates by category
  const groupedTemplates = filteredTemplates.reduce((acc, template) => {
    const cat = template.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(template);
    return acc;
  }, {});

  if (loading) {
    return (
      <AppLayout title="System Email Templates">
        <div className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>Loading...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="System Email Templates">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>
              Manage email templates sent to recipients. Templates support variables like {'{{recipient_name}}'}.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {templates.length === 0 && (
              <button
                onClick={initializeTemplates}
                disabled={initializing}
                className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90 transition-colors disabled:opacity-50`}
              >
                <RefreshCw className={`w-4 h-4 ${initializing ? 'animate-spin' : ''}`} />
                Initialize Default Templates
              </button>
            )}
            <button
              onClick={() => navigate('/admin/system-emails/new')}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90 transition-colors`}
            >
              <Plus className="w-4 h-4" />
              New Template
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm focus:border-[#A2FF00] outline-none`}
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`px-4 py-2 ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm focus:border-[#A2FF00] outline-none`}
            >
              <option value="all">All Categories</option>
              {Object.keys(categoryLabels).map(key => (
                <option key={key} value={key}>{categoryLabels[key]}</option>
              ))}
            </select>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className={`px-4 py-2 ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm focus:border-[#A2FF00] outline-none`}
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>

        {/* Category Cards */}
        {selectedCategory === 'all' && templates.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map(cat => {
              const Icon = categoryIcons[cat.key] || Mail;
              return (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCategory(cat.key)}
                  className={`${isDark ? 'bg-[#111] border-white/10 hover:border-[#A2FF00]/50' : 'bg-white border-gray-200 hover:border-green-400'} border rounded-sm p-4 text-left transition-colors`}
                >
                  <Icon className={`w-6 h-6 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-2`} />
                  <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium text-sm`}>{cat.name}</div>
                  <div className={`${isDark ? 'text-gray-500' : 'text-gray-400'} text-xs mt-1`}>{cat.count} templates</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Templates Grid */}
        {Object.keys(groupedTemplates).length > 0 ? (
          Object.entries(groupedTemplates).map(([category, catTemplates]) => {
            const Icon = categoryIcons[category] || Mail;
            return (
              <div key={category} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                  <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {categoryLabels[category]}
                  </h2>
                  <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'} text-sm`}>
                    ({catTemplates.length})
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {catTemplates.map(template => (
                    <div
                      key={template.template_id}
                      className={`${isDark ? 'bg-[#111] border-white/10 hover:border-[#A2FF00]/30' : 'bg-white border-gray-200 hover:border-green-300'} border rounded-sm overflow-hidden transition-colors`}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium truncate`}>
                              {template.name}
                            </h3>
                            <p className={`${isDark ? 'text-gray-500' : 'text-gray-400'} text-sm truncate mt-1`}>
                              {template.subject}
                            </p>
                          </div>
                          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            template.status === 'published'
                              ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                              : isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {template.status === 'published' ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                            {template.status}
                          </span>
                        </div>
                        
                        {/* User Editable Badge */}
                        {!template.is_user_editable && (
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>
                            System Only
                          </span>
                        )}
                      </div>
                      
                      <div className={`flex items-center justify-between px-4 py-3 ${isDark ? 'bg-black/50 border-t border-white/10' : 'bg-gray-50 border-t border-gray-200'}`}>
                        <button
                          onClick={() => setShowPreview(template)}
                          className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          <Eye className="w-4 h-4" />
                          Preview
                        </button>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/system-emails/${template.template_id}/edit`)}
                            className={`p-1.5 rounded ${isDark ? 'hover:bg-white/10 text-gray-400 hover:text-[#A2FF00]' : 'hover:bg-gray-200 text-gray-500 hover:text-green-600'}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteTemplate(template.template_id)}
                            className={`p-1.5 rounded ${isDark ? 'hover:bg-white/10 text-gray-400 hover:text-red-400' : 'hover:bg-gray-200 text-gray-500 hover:text-red-600'}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {templates.length === 0 ? (
              <div>
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No email templates found.</p>
                <p className="text-sm mt-2">Click &quot;Initialize Default Templates&quot; to get started.</p>
              </div>
            ) : (
              <p>No templates match your filters.</p>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className={`${previewTheme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-white'} rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${previewTheme === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
              <div>
                <h3 className={`font-bold ${previewTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {showPreview.name}
                </h3>
                <p className={`text-sm ${previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Preview how recipients will see this email
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className={`flex items-center p-1 rounded ${previewTheme === 'dark' ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <button
                    onClick={() => setPreviewTheme('light')}
                    className={`p-2 rounded ${previewTheme === 'light' ? (isDark ? 'bg-white/20' : 'bg-white shadow') : ''}`}
                  >
                    <Sun className={`w-4 h-4 ${previewTheme === 'light' ? 'text-yellow-500' : previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                  <button
                    onClick={() => setPreviewTheme('dark')}
                    className={`p-2 rounded ${previewTheme === 'dark' ? 'bg-white/20' : ''}`}
                  >
                    <Moon className={`w-4 h-4 ${previewTheme === 'dark' ? 'text-blue-400' : 'text-gray-500'}`} />
                  </button>
                </div>
                <button
                  onClick={() => setShowPreview(null)}
                  className={`px-4 py-2 ${previewTheme === 'dark' ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} rounded transition-colors`}
                >
                  Close
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              {/* Email Header */}
              <div className={`p-4 rounded-t border ${previewTheme === 'dark' ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`text-sm ${previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  <strong>Subject:</strong> {showPreview.subject}
                </div>
              </div>
              
              {/* Email Body */}
              <div className={`p-6 border-x border-b rounded-b ${previewTheme === 'dark' ? 'bg-black/50 border-white/10' : 'bg-white border-gray-200'}`}>
                <div 
                  className={`prose max-w-none ${previewTheme === 'dark' ? 'prose-invert' : ''}`}
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(showPreview.body) }}
                />
              </div>
              
              {/* Variable Hint */}
              <div className={`mt-4 p-3 rounded ${previewTheme === 'dark' ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-yellow-50 border border-yellow-200'}`}>
                <p className={`text-sm ${previewTheme === 'dark' ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  Note: Variables like {'{{recipient_name}}'} will be replaced with actual values when the email is sent.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default SystemEmailTemplates;
